/*
 * Copyright (c) 2025 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, October 2nd 2025, 2:47:56 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { ArtificialIntelligenceIcon } from "theme/icons";
import Stack from "@mui/material/Stack";
import { OSActionButton, StyledOSDialog, ActionDialog, useToggleState, OSAPIForm } from "ui-widgets";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import { FileText } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { AIAgentConfigurationManyToOnePicker } from "ai-workers/components";
import { useAIWorkersAPI } from "ai-workers/services/AIWorkersAPI";
import { getDefaultAIFormReviewAgent } from "lib/factories";
import { FormConfiguration, AIFormReviewAgent } from "lib/schemas";
import { EmptyAgentsCTA } from "./EmptyAgentsCTA";

type AIFormReviewAgentFormProps = {
  form: FormConfiguration;
} & Parameters<ReturnType<typeof useAIWorkersAPI>["useAIFormReviewAgentsAPIForm"]>[0];

export const AIFormReviewAgentForm = ({
  form,
  aiFormReviewAgentDefaultValues,
  aiFormReviewAgentFetchId,
  updateAIFormReviewAgentFromAPICallback,
}: AIFormReviewAgentFormProps) => {
  const { useAIFormReviewAgentsAPIForm } = useAIWorkersAPI();
  const useAPIFormReturn = useAIFormReviewAgentsAPIForm({
    aiFormReviewAgentDefaultValues: aiFormReviewAgentDefaultValues || getDefaultAIFormReviewAgent(form),
    aiFormReviewAgentFetchId,
    updateAIFormReviewAgentFromAPICallback,
  });
  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn}>
      <AIAgentConfigurationManyToOnePicker<AIFormReviewAgent>
        schema_field_key="ai_agent_configuration"
        label="AI Agent Configuration"
      />
    </OSAPIForm>
  );
};

interface AIFormReviewAgentListProps {
  form: FormConfiguration;
}

export const AIFormReviewAgentList = ({ form }: AIFormReviewAgentListProps) => {
  const [agentsList, setAgentList] = React.useState<AIFormReviewAgent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = React.useState<AIFormReviewAgent["id"]>(null);
  const { open, handleClose, handleOpen } = useToggleState();

  const { useAIFormReviewAgentAPI, useDeleteAIFormReviewAgents } = useAIWorkersAPI();
  const { deleteItem } = useDeleteAIFormReviewAgents();
  const onSelectAgent = (agent: AIFormReviewAgent) => {
    setSelectedAgentId(agent.id);
    handleOpen();
  };
  const onRemoveAgent = (agent: AIFormReviewAgent) => {
    deleteItem(agent.id, {
      onDelete: (id) => {
        setAgentList((prev) => prev.filter((ag) => ag.id !== id));
        setSelectedAgentId(null);
      },
      apiQueryParams: { form: form.id },
    });
  };

  const { makeRequest: getList, isSuccessfulAPIRequest: listFetchSuccess } =
    useAIFormReviewAgentAPI<AIFormReviewAgent[]>();

  const onLoadReviewAgentFromAPI = (agent: AIFormReviewAgent) => {
    setAgentList((prev) => {
      if (!prev.find((i) => i.id === agent.id)) {
        return [...prev, agent];
      }

      return prev.map((i) => {
        if (agent.id === i.id) return agent;
        return i;
      });
    });
  };

  React.useEffect(() => {
    if (!form?.id) return;
    getList({ apiQueryParams: { form: form.id }, setValidAPIResponse: setAgentList });
  }, [form?.id, getList]);

  const addAgent = (
    <OSActionButton variant="contained" size="large" actionStyle="add" onClick={handleOpen}>
      Configure new agent
    </OSActionButton>
  );

  const agentFormDialog = (
    <StyledOSDialog
      open={open}
      onClose={handleClose}
      popUpIcon={<ArtificialIntelligenceIcon fill={COLORSCHEME.primary} width={30} height={30} />}
      popUpTitle="Form Review AI Agent"
    >
      <AIFormReviewAgentForm
        form={form}
        aiFormReviewAgentDefaultValues={null}
        aiFormReviewAgentFetchId={selectedAgentId}
        updateAIFormReviewAgentFromAPICallback={onLoadReviewAgentFromAPI}
      />
    </StyledOSDialog>
  );

  return (
    <>
      {agentFormDialog}
      {listFetchSuccess && agentsList.length === 0 ? (
        <EmptyAgentsCTA ctaButtons={<>{addAgent}</>} />
      ) : (
        <Stack direction="column" spacing={2}>
          <List component="nav" aria-label="agents-list">
            {agentsList.map((agent) => (
              <FormReviewAgentListItem
                agent={agent}
                handleSelectAgent={onSelectAgent}
                handleRemoveAgent={onRemoveAgent}
              />
            ))}
          </List>
        </Stack>
      )}
    </>
  );
};

interface FormReviewAgentListItemProps {
  agent: AIFormReviewAgent;
  handleSelectAgent: (agent: AIFormReviewAgent) => void;
  handleRemoveAgent: (agent: AIFormReviewAgent) => void;
}

const FormReviewAgentListItem = ({ handleRemoveAgent, agent, handleSelectAgent }: FormReviewAgentListItemProps) => {
  const {
    handleClose: closeDeleteConfirmationFn,
    handleOpen: openDeleteConfirmationFn,
    open: deleteConfirmationOpened,
  } = useToggleState();

  return (
    <>
      <ListItemButton color={COLORSCHEME.secondaryVariant} key={`${agent.id}`}>
        <Grid container>
          <Grid item md={1}>
            <FileText color={COLORSCHEME.primary} />
          </Grid>
          <Grid item md={7}>
            <ListItemText primary={agent.ai_agent_configuration_object.name} />
          </Grid>
          <Grid item md={4}>
            <Stack direction="row" spacing={10}>
              <OSActionButton actionStyle="edit" variant="text" onClick={() => handleSelectAgent(agent)}>
                Edit Agent
              </OSActionButton>
              <OSActionButton actionStyle="delete" variant="text" onClick={openDeleteConfirmationFn}>
                Remove Agent
              </OSActionButton>
            </Stack>
          </Grid>
        </Grid>
      </ListItemButton>
      <ActionDialog
        open={deleteConfirmationOpened}
        onClose={closeDeleteConfirmationFn}
        title="Remove agent configuration for this form"
        dialogMessage="Are you sure you want to remove this configuraton?"
        severity="warning"
        confirmationAction={() => {
          handleRemoveAgent(agent);
          closeDeleteConfirmationFn();
        }}
      />
    </>
  );
};
